import { DeleteOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import MailModal from "@components/MailModal";
import { useObjects } from "@features/devices/api";
import {
  type FormValues,
  generateLogin,
  normalizeObjects,
  preparePayload,
  UserFormFields,
} from "@features/users";
import { useAdminUser, useCreateAdminUser, useUpdateAdminUser } from "@features/users/api";
import {
  useAdminDeleteUserPassword,
  useAdminSetUserPassword,
} from "@shared/api/generated/admin/admin";
import { getPrimaryContact } from "@shared/directoryUsers";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import EntityHeaderActions from "@shared/ui/entity-header-actions";
import PageLayout from "@shared/ui/page-layout";
import { getStoredUserEmail, setStoredUserEmail } from "@shared/userStorage";
import { useStoredUser } from "@shared/useStoredUser";
import { Alert, App as AntdApp, Button, Card, Form, Input, Popconfirm, Space, Tag } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

interface AdminUserDetailPageProps {
  profileMode?: boolean;
  profileUserId?: string;
}

const PasswordSection = ({
  userId,
  hasPassword,
  onChanged,
}: {
  userId: string;
  hasPassword: boolean;
  onChanged: () => void;
}) => {
  const { message } = AntdApp.useApp();
  const [password, setPassword] = useState("");
  const setMutation = useAdminSetUserPassword();
  const deleteMutation = useAdminDeleteUserPassword();

  const handleSet = async () => {
    try {
      await setMutation.mutateAsync({ id: userId, data: { password } });
      message.success("Пароль установлен");
      setPassword("");
      onChanged();
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id: userId });
      message.success("Пароль удалён");
      onChanged();
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  return (
    <Card
      title={
        <Space>
          <LockOutlined />
          Пароль
          {hasPassword ? (
            <Tag color="green">Установлен</Tag>
          ) : (
            <Tag color="default">Не установлен</Tag>
          )}
        </Space>
      }
      size="small"
    >
      <Space style={{ width: "100%" }} wrap>
        <Input.Password
          placeholder="Новый пароль (мин. 8 символов)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: 280 }}
        />
        <Button
          type="primary"
          onClick={handleSet}
          loading={setMutation.isPending}
          disabled={password.length < 8}
        >
          {hasPassword ? "Сменить пароль" : "Установить пароль"}
        </Button>
        {hasPassword && (
          <Popconfirm
            title="Удалить пароль?"
            description="Пользователь не сможет входить по паролю"
            onConfirm={handleDelete}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
              Удалить пароль
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Card>
  );
};

const AdminUserDetailPage = ({ profileMode = false, profileUserId }: AdminUserDetailPageProps) => {
  const { id: routeId } = useParams<{ id: string }>();
  const storedSelf = useStoredUser();
  const resolvedId = profileMode ? (profileUserId ?? storedSelf?.id) : routeId;
  const isNew = !profileMode && (!resolvedId || resolvedId === "new");
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();

  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(isNew);
  const suppressDirtyRef = useRef(false);
  const loginSeedRef = useRef(!profileMode && isNew ? generateLogin() : undefined);

  const canUseAdminProfile = !profileMode || storedSelf?.role === "admin";
  const userQuery = useAdminUser(canUseAdminProfile ? resolvedId : undefined);
  const createUser = useCreateAdminUser();
  const updateUser = useUpdateAdminUser();
  const objectsQuery = useObjects();
  const profileData = profileMode ? (userQuery.data ?? storedSelf) : userQuery.data;
  const profileReadOnly = profileMode && storedSelf?.role !== "admin";

  const [mailOpen, setMailOpen] = useState(false);
  const [mailDefaults, setMailDefaults] = useState({ subject: "", body: "" });
  const [mailRecipient, setMailRecipient] = useState<string | undefined>();

  useEffect(() => {
    if (isNew) {
      suppressDirtyRef.current = true;
      form.setFieldsValue({
        id: loginSeedRef.current,
        status: "pending",
        role: "user",
        objects: [],
      });
      suppressDirtyRef.current = false;
      return;
    }
    if (!profileData) return;
    suppressDirtyRef.current = true;
    form.setFieldsValue({
      ...profileData,
      email:
        getPrimaryContact(profileData, "email") ?? (profileMode ? getStoredUserEmail() : undefined),
      phone: getPrimaryContact(profileData, "phone"),
      status: profileData.status ?? "pending",
      role: profileData.role ?? "user",
      objects: normalizeObjects(profileData.objects),
    });
    setDirty(false);
    suppressDirtyRef.current = false;
  }, [form, isNew, profileData, profileMode]);

  const handleSubmit = async () => {
    if (profileReadOnly) {
      message.info("Профиль доступен только для просмотра");
      return;
    }
    if (profileMode && !profileData) {
      message.warning("Данные профиля ещё загружаются");
      return;
    }
    if (!profileMode && isNew && !loginSeedRef.current) loginSeedRef.current = generateLogin();
    try {
      const values = await form.validateFields();
      const payload = preparePayload(
        values,
        loginSeedRef.current ?? generateLogin(),
        profileMode ? profileData : undefined,
      );
      if (profileMode && profileData) {
        payload.objects = profileData.objects;
        payload.note = profileData.note;
      }
      const targetId = resolvedId && resolvedId !== "new" ? resolvedId : payload.id;
      setSubmitting(true);
      if (!profileMode && isNew) {
        await createUser.mutateAsync({ data: payload });
        message.success("Пользователь создан");
        navigate("/admin/users");
        return;
      }
      if (!targetId) {
        message.error("Не удалось определить пользователя");
        return;
      }
      await updateUser.mutateAsync({ id: targetId, data: payload });
      message.success(profileMode ? "Профиль обновлён" : "Пользователь обновлён");
      if (profileMode) setStoredUserEmail(payload.contacts?.find((c) => c.type === "email")?.value);
      setDirty(false);
      if (!profileMode) navigate("/admin/users");
    } catch (err) {
      message.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const fullName = [profileData?.last_name, profileData?.first_name, profileData?.middle_name]
    .filter((p) => p?.trim())
    .join(" ")
    .trim();
  const handleSendMail = () => {
    const emailField = (form.getFieldValue("email") as string | undefined)?.trim();
    const fallbackEmail = getPrimaryContact(profileData, "email");
    const recipient = emailField || fallbackEmail;
    if (!recipient && !profileData?.telegram_id) {
      message.warning("У пользователя нет email или Telegram");
      return;
    }
    setMailRecipient(recipient);
    setMailDefaults({
      subject: `AC Web — сообщение для ${fullName || "пользователя AC Web"}`,
      body: `Здравствуйте, ${fullName || "коллеги"}!\n\n`,
    });
    setMailOpen(true);
  };
  const canSendMail =
    !isNew &&
    (!!(form.getFieldValue("email") as string)?.trim() ||
      !!getPrimaryContact(profileData, "email") ||
      Boolean(profileData?.telegram_id));
  const pageTitle = profileMode
    ? "Профиль пользователя"
    : isNew
      ? "Новый пользователь"
      : `Пользователь ${resolvedId}`;
  const copyId = Form.useWatch("id", form) ?? resolvedId;
  const copyUrl =
    !profileMode && copyId && copyId !== "new"
      ? `/admin/users/${encodeURIComponent(copyId)}`
      : undefined;

  return (
    <PageLayout
      title={pageTitle}
      copyUrl={copyUrl}
      extra={
        <EntityHeaderActions
          prefix={
            !isNew && !profileMode ? (
              <Button icon={<MailOutlined />} onClick={handleSendMail} disabled={!canSendMail}>
                Отправить
              </Button>
            ) : null
          }
          onBack={() => (profileMode ? navigate("/settings/profile") : navigate(-1))}
          onSave={profileReadOnly ? undefined : handleSubmit}
          saveLoading={submitting}
          saveDisabled={!dirty || profileReadOnly}
        />
      }
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        {!isNew && userQuery.isLoading && (
          <Alert type="info" showIcon title="Загружаем данные пользователя..." />
        )}
        <UserFormFields
          form={form}
          profileMode={profileMode}
          profileData={profileData}
          profileReadOnly={profileReadOnly}
          isNew={isNew}
          objects={objectsQuery.data ?? []}
          objectsLoading={objectsQuery.isLoading}
          onDirtyChange={setDirty}
          suppressDirtyRef={suppressDirtyRef}
        />
        {!isNew && !profileMode && resolvedId && (
          <PasswordSection
            userId={resolvedId}
            hasPassword={userQuery.data?.has_password ?? false}
            onChanged={() => userQuery.refetch()}
          />
        )}
        <MailModal
          open={mailOpen}
          onClose={() => setMailOpen(false)}
          defaultEmail={mailRecipient}
          defaultTelegramId={profileData?.telegram_id ?? undefined}
          defaultTelegramUsername={profileData?.telegram_username ?? undefined}
          defaultUserId={profileData?.id ?? resolvedId}
          defaultSubject={mailDefaults.subject}
          defaultBody={mailDefaults.body}
        />
      </Space>
    </PageLayout>
  );
};

export default AdminUserDetailPage;
